/**
 * Toast queue + viewport. Mounted once at app root inside AuthProvider so
 * `useToast()` is available from any page/component.
 *
 *   const toast = useToast();
 *   toast.push({ variant: 'success', title: 'Saved', body: 'PCO 4.1 updated' });
 *
 * Auto-dismiss policy:
 *   success / info  → 5 seconds (overridable with `sticky: true`)
 *   warning / error → always sticky (require manual dismiss; these carry
 *                     consequences the user should acknowledge)
 *
 * Stacks top-right; new toasts append below older ones. Click anywhere
 * on the card (or the × button) to dismiss.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Toast, type ToastVariant } from './Toast';

interface ToastInput {
  variant: ToastVariant;
  title?: string;
  body: ReactNode;
  /** Force-sticky for success/info toasts (warning/error are always sticky). */
  sticky?: boolean;
  /** Override the auto-dismiss delay (ms) for this toast. Ignored when sticky.
   *  Defaults to AUTO_DISMISS_MS. */
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: string;
}

interface ToastApi {
  push: (input: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 5_000;

function isAutoDismiss(item: ToastInput): boolean {
  if (item.sticky) return false;
  return item.variant === 'success' || item.variant === 'info';
}

let toastCounter = 0;
function nextToastId(): string {
  toastCounter += 1;
  return `t${Date.now().toString(36)}_${toastCounter}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  // Hold the live timer handles so we can clear them on dismiss/unmount.
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput): string => {
      const id = nextToastId();
      const item: ToastItem = { ...input, id };
      setItems((prev) => [...prev, item]);
      if (isAutoDismiss(input)) {
        const handle = setTimeout(() => dismiss(id), input.durationMs ?? AUTO_DISMISS_MS);
        timersRef.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  // Clear all pending timers on unmount so we don't update state after teardown.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const handle of timers.values()) clearTimeout(handle);
      timers.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            title={t.title}
            body={t.body}
            onDismiss={() => dismiss(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast() called outside of <ToastProvider>');
  return ctx;
}
